import { DashboardPermissions } from "@/modules/dashboard/permissions";
import { ReservationPermissions } from "@/modules/reservations/permissions";
import { FlightPermissions } from "@/modules/flights/permissions";
import { HotelPermissions } from "@/modules/hotels/permissions";
import { PackagePermissions } from "@/modules/packages/permissions";
import { TransferPermissions } from "@/modules/transfers/permissions";
import { CustomerPermissions } from "@/modules/customers/permissions";
import { SupportPermissions } from "@/modules/support/permissions";
import { ReportPermissions } from "@/modules/reporting/permissions";
import { SettingsPermissions } from "@/modules/settings/permissions";
import { LandingPagePermissions } from "@/modules/landing-pages/permissions";
import { DealsPermissions } from "@/modules/deals/permissions";
import { TravelAnalyticsPermissions } from "@/modules/travel-analytics/permissions";
import { AdvancedAnalyticsPermissions } from "@/modules/advanced-analytics/permissions";
import { ProviderPermissions } from "@/modules/providers/permissions";
import { IssueSupportTemplatePermissions } from "@/modules/issue-support-templates/permissions";
import { RewardsPermissions } from "@/modules/rewards/permissions";
import { MarketingAssetPermissions, type MarketingAssetPermission } from "@/marketing/permissions";

/**
 * Every permission the admin understands, in `Travel:{Module}:{Action}` form - always
 * three segments, always under the `Travel:` namespace so these codes can coexist with
 * other Ticketon products in a shared permission store. `Travel:*` grants all of them.
 * The one exception is the marketing asset library (`SHARED_PERMISSION_CODES`), another
 * product's permission that this admin may also hand out because its landing page editor
 * picks images from that library.
 *
 * The module enums under `modules/<mod>/permissions.ts` stay the source of truth for
 * their own codes - this union and the catalog below are assembled from them so a new
 * module permission only has to be declared once.
 */
export type Permission =
  | DashboardPermissions
  | ReservationPermissions
  | FlightPermissions
  | HotelPermissions
  | PackagePermissions
  | TransferPermissions
  | CustomerPermissions
  | SupportPermissions
  | ReportPermissions
  | SettingsPermissions
  | LandingPagePermissions
  | DealsPermissions
  | TravelAnalyticsPermissions
  | AdvancedAnalyticsPermissions
  | ProviderPermissions
  | IssueSupportTemplatePermissions
  | RewardsPermissions
  | MarketingAssetPermission;

/**
 * Codes outside the `Travel:` namespace that the picker still offers. The backend accepts
 * exactly these (and nothing else foreign) from the travel admin's user management.
 */
export const SHARED_PERMISSION_CODES: Permission[] = [MarketingAssetPermissions.ASSETS];

/** Grants every permission in the system. */
export const FULL_ACCESS = "*";

export interface IPermissionDefinition {
  code: Permission;
  labelKey: string;
}

export interface IPermissionGroup {
  key: string;
  labelKey: string;
  /**
   * Wildcard codes that grant everything in this group (e.g. `Travel:Hotels:*`).
   * Usually one, but a group may span several namespaces - travel analytics keeps
   * providers and revenue as separate modules to stay at three segments.
   */
  allCodes: Permission[];
  permissions: IPermissionDefinition[];
}

/**
 * Grouped catalog rendered by the permission picker. Groups map 1:1 to modules so the
 * checkbox tree mirrors the sidebar.
 *
 * Every code is `Travel:{Module}:{Action}`. The wildcards in `allCodes` are deliberately
 * kept out of `permissions`: they are the group's "select all" shorthand, not
 * individually assignable permissions.
 */
export const PERMISSION_GROUPS: IPermissionGroup[] = [
  {
    key: "dashboard",
    labelKey: "permissions.groups.dashboard",
    allCodes: [DashboardPermissions.ALL],
    permissions: [
      { code: DashboardPermissions.READ, labelKey: "permissions.dashboard.read" },
    ],
  },
  {
    key: "reservations",
    labelKey: "permissions.groups.reservations",
    allCodes: [ReservationPermissions.ALL],
    permissions: [
      { code: ReservationPermissions.READ, labelKey: "permissions.reservations.read" },
      { code: ReservationPermissions.WRITE, labelKey: "permissions.reservations.write" },
      { code: ReservationPermissions.CANCEL, labelKey: "permissions.reservations.cancel" },
      { code: ReservationPermissions.REFUND, labelKey: "permissions.reservations.refund" },
    ],
  },
  {
    key: "flights",
    labelKey: "permissions.groups.flights",
    allCodes: [FlightPermissions.ALL],
    permissions: [
      { code: FlightPermissions.READ, labelKey: "permissions.flights.read" },
      { code: FlightPermissions.WRITE, labelKey: "permissions.flights.write" },
    ],
  },
  {
    key: "hotels",
    labelKey: "permissions.groups.hotels",
    allCodes: [HotelPermissions.ALL],
    permissions: [
      { code: HotelPermissions.READ, labelKey: "permissions.hotels.read" },
      { code: HotelPermissions.WRITE, labelKey: "permissions.hotels.write" },
    ],
  },
  {
    key: "packages",
    labelKey: "permissions.groups.packages",
    allCodes: [PackagePermissions.ALL],
    permissions: [
      { code: PackagePermissions.READ, labelKey: "permissions.packages.read" },
      { code: PackagePermissions.WRITE, labelKey: "permissions.packages.write" },
    ],
  },
  {
    key: "transfers",
    labelKey: "permissions.groups.transfers",
    allCodes: [TransferPermissions.ALL],
    permissions: [
      { code: TransferPermissions.READ, labelKey: "permissions.transfers.read" },
      { code: TransferPermissions.WRITE, labelKey: "permissions.transfers.write" },
    ],
  },
  {
    key: "customers",
    labelKey: "permissions.groups.customers",
    allCodes: [CustomerPermissions.ALL],
    permissions: [
      { code: CustomerPermissions.READ, labelKey: "permissions.customers.read" },
      { code: CustomerPermissions.WRITE, labelKey: "permissions.customers.write" },
    ],
  },
  {
    key: "support",
    labelKey: "permissions.groups.support",
    allCodes: [SupportPermissions.ALL],
    permissions: [
      { code: SupportPermissions.READ, labelKey: "permissions.support.read" },
      { code: SupportPermissions.WRITE, labelKey: "permissions.support.write" },
    ],
  },
  {
    key: "deals",
    labelKey: "permissions.groups.deals",
    allCodes: [DealsPermissions.DEALS_ALL],
    permissions: [
      { code: DealsPermissions.DEALS_READ, labelKey: "permissions.deals.read" },
      { code: DealsPermissions.DEALS_WRITE, labelKey: "permissions.deals.write" },
    ],
  },
  {
    key: "landing_pages",
    labelKey: "permissions.groups.landing_pages",
    allCodes: [LandingPagePermissions.ALL],
    permissions: [
      { code: LandingPagePermissions.READ, labelKey: "permissions.landing_pages.read" },
      { code: LandingPagePermissions.WRITE, labelKey: "permissions.landing_pages.write" },
    ],
  },
  {
    key: "reports",
    labelKey: "permissions.groups.reports",
    allCodes: [ReportPermissions.ALL],
    permissions: [
      { code: ReportPermissions.READ, labelKey: "permissions.reports.read" },
      { code: ReportPermissions.EXPORT, labelKey: "permissions.reports.export" },
    ],
  },
  {
    key: "travel_analytics",
    labelKey: "permissions.groups.travel_analytics",
    allCodes: [
      TravelAnalyticsPermissions.ALL,
      TravelAnalyticsPermissions.PROVIDERS_ALL,
      TravelAnalyticsPermissions.REVENUE_ALL,
    ],
    permissions: [
      { code: TravelAnalyticsPermissions.VIEW, labelKey: "permissions.travel_analytics.view" },
      { code: TravelAnalyticsPermissions.EXPORT, labelKey: "permissions.travel_analytics.export" },
      {
        code: TravelAnalyticsPermissions.PROVIDERS_VIEW,
        labelKey: "permissions.travel_analytics.providers_view",
      },
      {
        code: TravelAnalyticsPermissions.REVENUE_VIEW,
        labelKey: "permissions.travel_analytics.revenue_view",
      },
    ],
  },
  {
    key: "advanced_analytics",
    labelKey: "permissions.groups.advanced_analytics",
    allCodes: [AdvancedAnalyticsPermissions.ALL],
    permissions: [
      { code: AdvancedAnalyticsPermissions.VIEW, labelKey: "permissions.advanced_analytics.view" },
      {
        code: AdvancedAnalyticsPermissions.EXPLORE,
        labelKey: "permissions.advanced_analytics.explore",
      },
      {
        code: AdvancedAnalyticsPermissions.GOVERNANCE,
        labelKey: "permissions.advanced_analytics.governance",
      },
    ],
  },
  {
    key: "settings",
    labelKey: "permissions.groups.settings",
    allCodes: [SettingsPermissions.ALL],
    permissions: [
      { code: SettingsPermissions.READ, labelKey: "permissions.settings.read" },
      { code: SettingsPermissions.WRITE, labelKey: "permissions.settings.write" },
      { code: SettingsPermissions.USERS, labelKey: "permissions.settings.users" },
      { code: SettingsPermissions.ROLES, labelKey: "permissions.settings.roles" },
    ],
  },
  {
    key: "providers",
    labelKey: "permissions.groups.providers",
    allCodes: [ProviderPermissions.ALL],
    permissions: [
      { code: ProviderPermissions.READ, labelKey: "permissions.providers.read" },
      { code: ProviderPermissions.WRITE, labelKey: "permissions.providers.write" },
    ],
  },
  {
    key: "issue_support_templates",
    labelKey: "permissions.groups.issue_support_templates",
    allCodes: [IssueSupportTemplatePermissions.ALL],
    permissions: [
      {
        code: IssueSupportTemplatePermissions.READ,
        labelKey: "permissions.issue_support_templates.read",
      },
      {
        code: IssueSupportTemplatePermissions.WRITE,
        labelKey: "permissions.issue_support_templates.write",
      },
    ],
  },
  {
    key: "rewards",
    labelKey: "permissions.groups.rewards",
    allCodes: [RewardsPermissions.ALL],
    permissions: [
      { code: RewardsPermissions.VIEW, labelKey: "permissions.rewards.view" },
      { code: RewardsPermissions.MANAGE_PROGRAMS, labelKey: "permissions.rewards.manage_programs" },
      { code: RewardsPermissions.MANAGE_RULES, labelKey: "permissions.rewards.manage_rules" },
      { code: RewardsPermissions.MANAGE_CATALOG, labelKey: "permissions.rewards.manage_catalog" },
      { code: RewardsPermissions.VIEW_MEMBERS, labelKey: "permissions.rewards.view_members" },
      { code: RewardsPermissions.ADJUST_POINTS, labelKey: "permissions.rewards.adjust_points" },
      { code: RewardsPermissions.MANAGE_REDEMPTIONS, labelKey: "permissions.rewards.manage_redemptions" },
      { code: RewardsPermissions.VIEW_AUDIT, labelKey: "permissions.rewards.view_audit" },
    ],
  },
  {
    // The marketing asset library is one grant, so the assignable code is the module
    // wildcard itself; `Marketing:*` (workspace owners) covers it the usual way.
    key: "marketing_assets",
    labelKey: "permissions.groups.marketing_assets",
    allCodes: [MarketingAssetPermissions.ALL],
    permissions: [
      { code: MarketingAssetPermissions.ASSETS, labelKey: "permissions.marketing_assets.all" },
    ],
  },
];

/** Every individually assignable permission code, flattened. */
export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap(
  (group) => group.permissions.map((permission) => permission.code)
);

/** Lookup from code to its group, used to render a permission outside the tree. */
export const PERMISSION_GROUP_BY_CODE: Record<string, IPermissionGroup> = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((group) =>
    group.permissions.map((permission) => [permission.code, group] as const)
  )
);

/**
 * True when `granted` satisfies `required`, honouring wildcards at every namespace
 * depth: `*`, `Travel:*` and `Travel:Deals:*` all satisfy `Travel:Deals:Read`.
 */
export function permissionMatches(granted: string, required: string): boolean {
  if (granted === FULL_ACCESS || granted === required) {
    return true;
  }

  const segments = required.split(":");

  return segments
    .slice(0, -1)
    .some(
      (_, index) => granted === `${segments.slice(0, index + 1).join(":")}:${FULL_ACCESS}`
    );
}
